import styled from 'styled-components';
import { Row, Col, Collapse } from 'antd';
import { useSelector } from 'react-redux';
import CompanySkeleton from 'Components/common/skeleton/CompanySkeleton';
import GlobalButton from 'Components/common/components/GlobalButton';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import { IsMobile, parseColorGradientMixture } from 'Utils/UtilityFunctions';
import PeopleWidgetWithOutHolo from 'Components/common/components/PeopleWidgetWithOutHolo';
import BackIcon from 'Components/common/assets/svgIcons/BackIcon';
import CompanyCardLarge from '../../common/components/CompanyCardLarge';
import {
  secondaryFont, primaryFont, primaryHeadingColor, wLight, maxWidth, retina, maxMobile, maxTablet,
} from '../../../Utils/utilsStyle';
import PeopleIcon from '../svgIcons/PeopleIcon';
import { selectRecommendedCompanies, selectRecommendedCompaniesLoading } from '../../../Selector/CompaniesSelector';
import { Company } from '../../../Types/CompaniesTypes';
// import { parseColorGradients } from '../../../Utils/UtilityFunctions';

const StyleWrapper = styled.div`
  .fancy-box {
    border-radius: 10px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 23px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
     padding: 17px 25px 25px 25px;
    }
    @media all and (max-width: ${maxMobile}) {
     padding: 17px 16px 16px 16px;
    }
    margin-top: 50px;
    h1 {
      font-family: ${secondaryFont};
      color: ${primaryHeadingColor};
      font-size: 22px;
      margin-bottom: 0;
      margin-left: 10px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 19px;
      }
    }
    p {
      font-family: ${primaryFont};
      color: #5e7079;
    }
    input {
      font-size: 16px;
      padding: 7px 12px;
    }
    label {
      font-size: 12px;
    }
    h4.recommended-name{
      color:${primaryHeadingColor};
      font-weight:${wLight};
      font-family:${secondaryFont};
      margin-bottom:0;
      font-size:22px;
      line-height: 38px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
       font-size: 18px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 19px;
      }
    }
    .company-item {
      margin-bottom: 24px;
    }
    .ant-collapse-header, .ant-collapse-content-box {
      padding: 0px !important;
      align-items: center;
    }

    .ant-collapse-header {
      @media all and (max-width: ${maxMobile}) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .ant-collapse-extra {
        @media all and (max-width: ${maxMobile}) {
          width: 100%;
        }
        button {
          @media all and (max-width: ${maxMobile}) {
            width: 100%;
            text-align: center;
            justify-content: center;
            margin-top: 15px;
          }
        }
      }
    }
    .ant-collapse-item {
      .ant-collapse-arrow {
        display: none !important;
      }
      .heading-collapse-c {
        .heading-icon {
          display: none;
        }
      }
      &.ant-collapse-item-active {
        .user-images {
          display: none;
        }
        .ant-collapse-header {
          width: 100%;
        }
        .heading-collapse-c {
          .heading-icon {
            display: block;
            margin-right: 15px;
            margin-top: 5px;
          }
        }
        .ant-collapse-extra {
          display: none;
        }
      }
    }
    .user-images{
    position: relative;
    margin-right:13px;
    z-index:0;
    > div{
        padding:2px !important;
        &:nth-child(1){
          z-index:5;
        }
        &:nth-child(2){
          z-index:4;
          margin-left: -10px;
        }
        &:nth-child(3){
          z-index:3;
          margin-left: -10px;
        }
        &:nth-child(4){
          z-index:2;
          margin-left: -10px;
        }
        &:nth-child(5){
          z-index:1;
          margin-left: -10px;
        }
    }
  }
  }
`;

const CompaniesSection = function CompaniesSection() {
  const companies: Company[] = useSelector(selectRecommendedCompanies);
  const loading: boolean = useSelector(selectRecommendedCompaniesLoading);
  const { Panel } = Collapse;
  const isMob = IsMobile();

  const getCompaniesLoadingSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 10; i += 1) {
      skeletons.push(
        <div key={`skeleton${i}`}>
          <Col span={4} className="gutter-row">
            <CompanySkeleton />
          </Col>
        </div>,
      );
    }
    return (
      <Row gutter={[24, 24]} className="Grid-width-row">
        {skeletons}
      </Row>
    );
  };

  return (
    <StyleWrapper>
      <div className="fancy-box">
        <Collapse ghost>
          <Panel
            header={(
              <div className="d-flex heading-collapse-c">
                <div className="heading-icon">
                  {isMob ? (
                    <BackIcon />
                  ) : (
                    <PeopleIcon />
                  )}
                </div>

                <div className="d-flex align-items-center user-images">
                  {loading ? (
                    <>
                      {[1, 2, 3, 4, 5].map((id) => (
                        <SkeletonCustom key={id} width="40px" height="40px" diameter="50%" margin="0px" />
                      ))}
                    </>
                  )
                    : companies.slice(0, 5).map((company) => (
                      <PeopleWidgetWithOutHolo
                        key={company?.companyId}
                        diameter="40px"
                        diameterSm="40px"
                        diameterXsm="35px"
                        marginSm="0px 3px 0px 3px"
                        margin="0"
                        wrapperColor={parseColorGradientMixture(company.companyPersonality)}
                        logo={company.companyLogo}
                        companyId={String(company.companyId) !== '' ? company.companyId : 0}
                        personForHover
                        showPersonName
                        personName={company.companyName}
                      />
                    ))}
                </div>
                <h4
                  className="recommended-name d-flex align-items-center"
                >
                  {loading ? (<SkeletonCustom width="500px" height="20px" margin="0px" />) : 'We’ve found some Companies you might be interested in…'}
                </h4>
              </div>
            )}
            key="1"
            extra={
              <GlobalButton title="See Recommended" bgColor="#001760" borderRadius="25px" PaddingSm="5px 15px 5px 10px" />
            }
          >
            <div className="companiesSection">
              <Row className="Grid-width-row py-2 white-layer-col" gutter={[24, 24]}>
                {
                  loading ? getCompaniesLoadingSkeleton() : null
                }
                {
                  companies?.length ? (
                    companies.map((company) => (
                      <Col
                        key={company.companyId}
                        xxl={{ span: 4 }}
                        xl={{ span: 4 }}
                        lg={{ span: 6 }}
                        className="gutter-row"
                      >
                        <CompanyCardLarge
                          companyData={company}
                          showDrawerIcon={!!company.companyNotes}
                        />
                      </Col>
                    ))
                  ) : null
                }
              </Row>
            </div>
          </Panel>
        </Collapse>
      </div>
    </StyleWrapper>
  );
};

export default CompaniesSection;
